import { useState, useEffect, useCallback, useRef } from 'react';
import { navigateTo } from './useHashRouter';

const AUTH_KEY = 'clarime_auth';
const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes

/**
 * useAuth - Manages authentication state
 * 
 * Stores auth in localStorage so it persists across refreshes.
 * Tracks user activity and auto-signs out after 10 minutes of inactivity.
 * 
 * Usage:
 *   const { isSignedIn, user, signIn, signOut } = useAuth();
 */
export function useAuth() {
  const [authState, setAuthState] = useState(() => {
    try {
      const stored = localStorage.getItem(AUTH_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Check if session has expired from inactivity
        if (parsed.lastActivity && Date.now() - parsed.lastActivity > INACTIVITY_TIMEOUT) {
          localStorage.removeItem(AUTH_KEY);
          localStorage.removeItem('auth_token');
          return null;
        }
        return parsed;
      }
    } catch (e) {
      localStorage.removeItem(AUTH_KEY);
    }
    return null;
  });

  const timeoutRef = useRef(null);

  // Update last activity timestamp
  const touchActivity = useCallback(() => {
    if (!authState) return;
    const updated = { ...authState, lastActivity: Date.now() };
    localStorage.setItem(AUTH_KEY, JSON.stringify(updated));
    // Don't trigger re-render for activity touches
  }, [authState]);

  // Reset inactivity timer
  const resetTimer = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (!authState) return;

    touchActivity();

    timeoutRef.current = setTimeout(() => {
      // Auto sign out
      localStorage.removeItem(AUTH_KEY);
      localStorage.removeItem('auth_token');
      setAuthState(null);
      navigateTo('/join');
    }, INACTIVITY_TIMEOUT);
  }, [authState, touchActivity]);

  // Listen for user activity
  useEffect(() => {
    if (!authState) return;

    const events = ['mousedown', 'touchstart', 'keydown', 'scroll', 'mousemove'];
    
    // Throttle activity tracking to avoid excessive writes
    let lastTouch = 0;
    const throttledReset = () => {
      const now = Date.now();
      if (now - lastTouch > 30000) { // Update at most every 30 seconds
        lastTouch = now;
        resetTimer();
      }
    };

    events.forEach(event => {
      window.addEventListener(event, throttledReset, { passive: true });
    });

    // Start the initial timer
    resetTimer();

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, throttledReset);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [authState, resetTimer]);

  // Sign in - called after successful auth flow
  const signIn = useCallback((userData) => {
    const state = {
      phone: userData.phone || '',
      email: userData.email || '',
      firstName: userData.firstName || '',
      isDemoMode: userData.isDemoMode || false,
      lastActivity: Date.now(),
      signedInAt: Date.now(),
    };
    localStorage.setItem(AUTH_KEY, JSON.stringify(state));

    // Store the auth token for API requests (apiService reads this)
    if (userData.idToken) {
      localStorage.setItem('auth_token', userData.idToken);
    }

    setAuthState(state);
  }, []);

  // Sign out
  const signOut = useCallback(() => {
    localStorage.removeItem(AUTH_KEY);
    localStorage.removeItem('auth_token');
    // Clear the session flag so AuthFlow can offer biometric on the next
    // sign-in attempt. The main.jsx initial-mount auto-biometric won't fire
    // here (it only runs once on Root mount), so if the user signs back in
    // within the same session and has biometric enrolled, AuthFlow's
    // biometric step is their path — we want it available.
    sessionStorage.removeItem('clarime_initial_biometric_attempted');
    // The user chose to sign out — AuthFlow must land on the email step,
    // not auto-fire Face ID. Auto-firing here re-signed the user straight
    // back in, which made Sign Out look broken (build-32 bug). Face ID
    // stays enrolled and available behind an explicit button.
    sessionStorage.setItem('clarime_explicit_signout', '1');
    setAuthState(null);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    navigateTo('/join');
  }, []);

  // When any API call comes back 401 (token expired server-side, or the
  // account was deleted), drop the local session and route to sign-in.
  // The flag tells AuthFlow to show a "session expired" notice on the
  // email step and to skip the biometric auto-prompt — Face ID would just
  // replay the same dead token.
  useEffect(() => {
    const onSessionExpired = () => {
      if (!localStorage.getItem(AUTH_KEY)) return; // already signed out
      sessionStorage.setItem('clarime_session_expired', '1');
      localStorage.removeItem(AUTH_KEY);
      localStorage.removeItem('auth_token');
      setAuthState(null);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      navigateTo('/join');
    };
    window.addEventListener('clarime:session-expired', onSessionExpired);
    return () => window.removeEventListener('clarime:session-expired', onSessionExpired);
  }, []);

  return {
    isSignedIn: !!authState,
    user: authState ? { phone: authState.phone, email: authState.email, firstName: authState.firstName, isDemoMode: authState.isDemoMode || false } : null,
    signIn,
    signOut,
  };
}

/**
 * Check if a user has previously signed up (for "Welcome back" detection)
 */
export function hasExistingAccount() {
  try {
    const history = localStorage.getItem('clarime_auth_history');
    return !!history;
  } catch {
    return false;
  }
}

/**
 * Record that this email/phone has been used before
 */
export function recordAccountHistory(identifier) {
  try {
    const history = JSON.parse(localStorage.getItem('clarime_auth_history') || '[]');
    if (!history.includes(identifier)) {
      history.push(identifier);
      localStorage.setItem('clarime_auth_history', JSON.stringify(history));
    }
  } catch {
    localStorage.setItem('clarime_auth_history', JSON.stringify([identifier]));
  }
}
